import { useMemo } from 'react';
import { useRouteLoaderData } from '@remix-run/react';
import type { Feed, FeedEntry } from '~/services/layout';
import type { loader as rootLoader } from '~/root.tsx';
import {
  buildFavoritesMenuItem,
  ENTRY_ACTIONS_IDENTIFIER,
  getResolvedPopupMenuState,
  type FavoritesMenuItem,
  type PopupMenuResolvedState,
} from '../utils/resolve-popup-menu-actions';

export type UsePopupMenuActionsFeedArgs = {
  /** the feed entry the menu acts on */
  entry: FeedEntry;
  /** whether the entry is a favourite (drives the favourites item label) */
  isFavorite: boolean;
  /** whether the favourites item may be shown for the entry */
  favoritesAvailable: boolean;
  isLoggedIn: boolean;
};

export type PopupMenuFeedState = {
  feed: Feed;
  hasRenderableActions: boolean;
};

/**
 * Resolves the popup menu feed from the popup-menu plugin's
 * `popup_menu_actions` configuration, independent of any cell action.
 * A missing or empty configuration falls back to the entry's own actions.
 * @returns the single-entry popup feed and whether it has renderable actions
 */
export function usePopupMenuActionsFeed({
  entry,
  isFavorite,
  favoritesAvailable,
  isLoggedIn,
}: UsePopupMenuActionsFeedArgs): PopupMenuFeedState {
  const rootLoaderData = useRouteLoaderData<typeof rootLoader>('root');
  const favoritesAction = rootLoaderData?.favoritesAction;

  const popupMenuActionsConfig: string =
    rootLoaderData?.popupMenuConfig?.customConfiguration?.popup_menu_actions ||
    ENTRY_ACTIONS_IDENTIFIER;

  const favoritesItem: FavoritesMenuItem = useMemo(() => {
    return buildFavoritesMenuItem({
      defaultLabel: favoritesAction?.localizations?.default_label,
      activeLabel: favoritesAction?.localizations?.active_label,
      isFavorite,
      isLoggedIn,
    });
  }, [favoritesAction, isFavorite, isLoggedIn]);

  return useMemo((): PopupMenuFeedState => {
    const { contextualEntry, hasRenderableActions }: PopupMenuResolvedState =
      getResolvedPopupMenuState({
        popupMenuActionsConfig,
        entry,
        favoritesAvailable,
        favoritesItem,
      });

    return {
      feed: { entry: [contextualEntry] } as Feed,
      hasRenderableActions,
    };
  }, [popupMenuActionsConfig, entry, favoritesAvailable, favoritesItem]);
}
